
import { supabase } from "@/integrations/supabase/client";
import { UserProduct } from "@/types";
import { isValidUUID } from "@/utils/validation";
import { getCachedUserId } from "@/services/auth/sessionCache";
import { normalizePhotoUrl } from "@/utils/normalizePhotoUrl";

interface MostPurchasedRaw {
  product_id: string;
  count: number;
}

// Get user products
export const getUserProducts = async (): Promise<UserProduct[]> => {
  const userId = await getCachedUserId();
  if (!userId) {
    return [];
  }
  const { data, error } = await supabase
    .from("user_products_with_categories")
    .select("*")
    .eq("user_id", userId);

  if (error) {
    console.error("Error fetching products:", error);
    return [];
  }

  // Map the data to match the UserProduct type
  return (data || []).map(item => ({
    id: item.id,
    userId: item.user_id,
    name: item.name,
    defaultQuantity: item.default_quantity,
    defaultUnit: item.default_unit,
    photo: normalizePhotoUrl(item.photo),
    barcode: item.barcode,
    openfoodfacts_id: item.openfoodfacts_id,
    attributes: item.attributes || [],
    categoryId: item.category_id,
    createdAt: item.created_at,
    category: item.category_id ? {
      id: item.category_id,
      name: item.category_name,
      icon: item.category_icon,
      color: item.category_color,
      textColor: item.category_text_color,
      position: item.category_position,
    } : undefined
  }));
};

// Get most purchased products
export const getMostPurchasedProducts = async (days: number = 30, limit: number = 10) => {
  try {
    const { data, error } = await supabase.rpc(
      'get_most_purchased_products',
      {
        days_ago: days,
        limit_count: limit
      }
    );

    if (error) throw error;

    const typedData = data as MostPurchasedRaw[] | null;

    if (!typedData || typedData.length === 0) {
      return [];
    }
    // Get product details
    const productIds = typedData.map((item: any) => item.product_id);
    const { data: productsData } = await supabase
      .from('user_products')
      .select('id, name')
      .in('id', productIds);

    return typedData.map((item: any) => {
      const product = productsData?.find(p => p.id === item.product_id);
      return {
        id: item.product_id,
        name: product?.name || 'Prodotto sconosciuto',
        purchase_count: item.count
      };
    });

  } catch (error) {
    console.error('Error fetching most purchased products:', error);
    return [];
  }
};

// Add a new product
export const addProduct = async (productData: any): Promise<any> => {
  // Ensure categoryId is properly formatted as UUID or null
  let categoryId = null;
  if (productData.categoryId && productData.categoryId !== 'none') {
    if (isValidUUID(productData.categoryId)) {
      categoryId = productData.categoryId;
    } else {
      console.warn("Invalid UUID format for categoryId:", productData.categoryId);
    }
  }
  // Check for existing product with same name and category (case-insensitive)
  let existingQuery = supabase
    .from("user_products")
    .select("*")
    .eq("user_id", productData.userId)
    .ilike("name", productData.name);

  if (categoryId) {
    existingQuery = existingQuery.eq("category_id", categoryId);
  } else {
    existingQuery = existingQuery.is("category_id", null);
  }

  const { data: existingProduct, error: existingError } = await existingQuery.maybeSingle();

  if (existingError) {
    console.error("Error checking existing product:", existingError);
    return null;
  }

  if (existingProduct) {
    console.info(`[addProduct] Product already exists: "${productData.name}" in category ${categoryId || 'none'}, returning existing ID: ${existingProduct.id}`);
    return {
      id: existingProduct.id,
      userId: existingProduct.user_id,
      name: existingProduct.name,
      defaultQuantity: existingProduct.default_quantity,
      defaultUnit: existingProduct.default_unit,
      photo: normalizePhotoUrl(existingProduct.photo),
      barcode: existingProduct.barcode,
      openfoodfacts_id: existingProduct.openfoodfacts_id,
      categoryId: existingProduct.category_id,
      createdAt: existingProduct.created_at
    };
  }

  const { data, error } = await supabase
    .from("user_products")
    .insert([{
      user_id: productData.userId,
      name: productData.name,
      default_quantity: productData.defaultQuantity,
      default_unit: productData.defaultUnit,
      photo: productData.photo,
      barcode: productData.barcode,
      openfoodfacts_id: productData.openfoodfactsId,
      category_id: categoryId,
      attributes: productData.attributes || []
    }])
    .select()
    .single();

  if (error) {
    // ✅ NUOVO: Fallback per errore di vincolo unico - controlla e ritorna prodotto esistente
    if (error.code === '23505' || error.message.includes('unique constraint')) {
      console.warn(`[addProduct] Unique constraint violation for "${productData.name}": attempting to fetch existing product`, error);
      
      // Prova di nuovo a cercare il prodotto che ha causato il conflitto
      const { data: conflictProduct } = await supabase
        .from("user_products")
        .select("*")
        .eq("user_id", productData.userId)
        .ilike("name", productData.name)
        .maybeSingle();
      
      if (conflictProduct) {
        console.info(`[addProduct] Conflict resolved: returning existing product ${conflictProduct.id}`);
        return {
          id: conflictProduct.id,
          userId: conflictProduct.user_id,
          name: conflictProduct.name,
          defaultQuantity: conflictProduct.default_quantity,
          defaultUnit: conflictProduct.default_unit,
          photo: normalizePhotoUrl(conflictProduct.photo),
          barcode: conflictProduct.barcode,
          openfoodfacts_id: conflictProduct.openfoodfacts_id,
          categoryId: conflictProduct.category_id,
          createdAt: conflictProduct.created_at
        };
      }
    }
    console.error("Error adding product:", error);
    return null;
  }

  return {
    id: data.id,
    userId: data.user_id,
    name: data.name,
    defaultQuantity: data.default_quantity,
    defaultUnit: data.default_unit,
    photo: normalizePhotoUrl(data.photo),
    barcode: data.barcode,
    openfoodfacts_id: data.openfoodfacts_id,
    categoryId: data.category_id,
    createdAt: data.created_at
  };
};

// Update an existing product
export const updateProduct = async (productId: string, updates: any): Promise<boolean> => {
  // Convert from UserProduct fields to database fields
  const dbUpdates: any = {};
  
  if (updates.name !== undefined) dbUpdates.name = updates.name;
  if (updates.defaultQuantity !== undefined) dbUpdates.default_quantity = updates.defaultQuantity;
  if (updates.defaultUnit !== undefined) dbUpdates.default_unit = updates.defaultUnit;
  if (updates.photo !== undefined) dbUpdates.photo = normalizePhotoUrl(updates.photo);
  if (updates.barcode !== undefined) dbUpdates.barcode = updates.barcode;
  if (updates.openfoodfacts_id !== undefined) dbUpdates.openfoodfacts_id = updates.openfoodfacts_id;
  if (updates.categoryId !== undefined) dbUpdates.category_id = updates.categoryId;
  if (updates.attributes !== undefined) dbUpdates.attributes = updates.attributes;

  try {
    const userId = await getCachedUserId();
    if (!userId) {
      return false;
    }

    const { error } = await supabase
      .from('user_products')
      .update(dbUpdates)
      .eq('id', productId)
      .eq('user_id', userId);

    if (error) {
      console.error("Error updating product:", error);
      return false;
    }

    return true;
  } catch (err) {
    console.error("Error updating product:", err);
    return false;
  }
};

// Delete a product
export const deleteProduct = async (productId: string): Promise<UserProduct | null> => {
  const userId = await getCachedUserId();
  if (!userId) {
    return null;
  }

  // Fetch product before deletion
  const { data: productData, error: fetchError } = await supabase
    .from('user_products')
    .select('*')
    .eq('id', productId)
    .eq('user_id', userId)
    .single();

  if (fetchError) {
    console.error('Error fetching product before delete:', fetchError);
    return null;
  }

  const { error } = await supabase
    .from('user_products')
    .delete()
    .eq('id', productId)
    .eq('user_id', userId);

  if (error) {
    console.error('Error deleting product:', error);
    return null;
  }

  // Emit event for server-side deletion
  window.dispatchEvent(new CustomEvent('serverProductDeleted', {
    detail: { productId }
  }));

  return {
    id: productData.id,
    userId: productData.user_id,
    name: productData.name,
    defaultQuantity: productData.default_quantity,
    defaultUnit: productData.default_unit,
    photo: normalizePhotoUrl(productData.photo),
    barcode: productData.barcode,
    openfoodfacts_id: productData.openfoodfacts_id,
    attributes: productData.attributes || [],
    categoryId: productData.category_id,
    createdAt: productData.created_at
  };
};

// Delete all products belonging to a category
export const deleteProductsByCategory = async (categoryId: string): Promise<boolean> => {
  const userId = await getCachedUserId();
  if (!userId) {
    return false;
  }

  const { error } = await supabase
    .from('user_products')
    .delete()
    .eq('category_id', categoryId)
    .eq('user_id', userId);

  if (error) {
    console.error('Error deleting products by category:', error);
    return false;
  }
  return true;
};

// Aliases for backward compatibility
export const addUserProduct = addProduct;
export const updateUserProduct = updateProduct;
export const deleteUserProduct = deleteProduct;
